import React, { useState, useRef, useEffect } from 'react';
import styles from './AgentFreeform.styles';
import { AgentConversation, AgentChatMessage } from './Agent.types';
import { AgentChat } from './AgentChat/AgentChat';
import { useDispatch } from 'react-redux';
import { update } from 'src/services/lenses/Lenses.actions';
import { planTrackerLens } from 'src/services/lenses/lenses';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { ASST_PLAN_REFRESHING_PAGE } from 'src/pages/AssortmentBuild/Planning.types';

export interface AgentFreeformProps {
  conversations?: AgentConversation[];
  onSendMessage?: (message: { convoId: string; message: AgentChatMessage }) => void;
}

const AgentFreeform: React.FC<AgentFreeformProps> = ({ conversations = [], onSendMessage }) => {
  const [inputValue, setInputValue] = useState('');
  const [currentConvoId, setCurrentConvoId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Always use the last conversation
  useEffect(() => {
    if (conversations.length > 0) {
      const lastConversation = conversations[conversations.length - 1];
      setCurrentConvoId(lastConversation.convoId);
    }
  }, [conversations]);

  // Get current conversation's messages
  const currentConversation = conversations.find((c) => c.convoId === currentConvoId);
  const messages = currentConversation?.messages || [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      const data = lastMsg.additionalData;
      if (data != null && data.length > 0) {
        data.forEach((d) => {
          switch (d.type) {
            case 'refresh':
              dispatch(
                update(
                  planTrackerLens.modify((pt) => {
                    return {
                      ...pt,
                      askForRefresh: false,
                      refreshRequestedAt: Date.now(),
                    };
                  }),
                  ASST_PLAN_REFRESHING_PAGE,
                ),
              );
              break;
            case 'link':
              const url = d.params?.dataApi?.url;
              if (!isEmpty(url)) {
                navigate(url);
              }
              break;
            default:
              break;
          }
        });
      }
    }
  }, [messages, navigate, dispatch]);

  const handleSendMessage = () => {
    if (inputValue.trim() === '' || !currentConvoId) return;

    const newMessage: AgentChatMessage = {
      message: inputValue,
      type: 'user',
    };

    if (onSendMessage) {
      onSendMessage({ convoId: currentConvoId, message: newMessage });
    }

    setInputValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className={styles.agentFreeformContainer}>
      <div className={styles.messagesContainer}>
        {messages.map((message, idx) => (
          <AgentChat key={idx} type={message.type} message={message.message} />
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className={styles.inputContainer}>
        <input
          type="text"
          className={styles.inputField}
          placeholder="Ask anything"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <button
          className={styles.sendButton}
          onClick={handleSendMessage}
          disabled={inputValue.trim() === '' || !currentConvoId}
        >
          <i className="fas fa-arrow-up" />
        </button>
      </div>
    </div>
  );
};

export default AgentFreeform;
